import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, Edit, Trash2, Check, X, Truck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FMS_FEATURE_CATEGORIES = {
  tracking: {
    title: 'GPS & Tracking',
    features: [
      { key: 'live_gps_tracking', label: 'Live GPS-Tracking', description: 'Echtzeit-Positionierung aller Fahrzeuge' },
      { key: 'route_history', label: 'Routen-Historie', description: 'Verlauf gefahrener Strecken' },
      { key: 'geo_fencing', label: 'Geo-Fencing', description: 'Automatische Warnungen bei Zonenüberschreitungen' },
      { key: 'traffic_integration', label: 'Verkehrsdaten-Integration', description: 'Optimierte Tourenplanung' }
    ]
  },
  diagnostics: {
    title: 'OBD & Diagnose',
    features: [
      { key: 'obd_remote_diagnostics', label: 'OBD-Fernauslesung', description: 'Remote-Diagnose aller Fahrzeuge' },
      { key: 'obd_live_diagnosis', label: '🚀 OBD Live-Diagnose (NEU)', description: 'Live-Daten mit CarCortex OBD-Dongle - GAMECHANGER Feature' },
      { key: 'error_code_alerts', label: 'Fehlercode-Benachrichtigungen', description: 'Automatische Warnungen bei Problemen' },
      { key: 'predictive_maintenance', label: 'Predictive Maintenance', description: 'KI-gestützte Wartungsvorhersage' },
      { key: 'fuel_efficiency_tracking', label: 'Kraftstoff-Tracking', description: 'Verbrauchsanalyse & Optimierung' }
    ]
  },
  management: {
    title: 'Verwaltung & Planung',
    features: [
      { key: 'vehicle_booking', label: 'Fahrzeugpool-Buchung', description: 'Verwaltung von Fahrzeugbuchungen' },
      { key: 'maintenance_scheduling', label: 'Wartungsplanung', description: 'Automatische Terminverwaltung' },
      { key: 'driver_management', label: 'Fahrerverwaltung', description: 'Fahrer-Profile & Zuweisungen' },
      { key: 'document_management', label: 'Dokumentenverwaltung', description: 'Fahrzeugpapiere & Versicherungen' }
    ]
  },
  analytics: {
    title: 'Analytics & Reporting',
    features: [
      { key: 'driver_scoring', label: 'Fahrer-Effizienz-Score', description: 'Bewertung des Fahrverhaltens' },
      { key: 'cost_analysis', label: 'Kosten-Analyse', description: 'TCO & Kosten pro km' },
      { key: 'usage_reports', label: 'Auslastungs-Reports', description: 'Fahrzeugnutzung & Standzeiten' },
      { key: 'custom_reports', label: 'Custom Reports', description: 'Individuelle Report-Erstellung' },
      { key: 'api_access', label: 'API-Zugang', description: 'REST-API für Integrationen' }
    ]
  }
};

export const AdminFMSPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const fmsPlans = response.data.filter(plan => plan.planType === 'fms');
      setPlans(fmsPlans);
    } catch (error) {
      toast.error('Fehler beim Laden der Pläne');
      console.error('Fehler beim Laden der Pläne:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDefaultFeatures = () => {
    const features = {};
    Object.values(FMS_FEATURE_CATEGORIES).forEach(category => {
      category.features.forEach(feature => {
        features[feature.key] = false;
      });
    });
    return features;
  };

  const handleCreate = async () => {
    try {
      const token = localStorage.getItem('token');

      const planData = {
        name: editingPlan.name,
        price: parseFloat(editingPlan.price),
        currency: editingPlan.currency === '€' ? 'EUR' : 'USD',
        billing_interval: editingPlan.period === 'Jahr' ? 'yearly' : editingPlan.period === 'Einmalig' ? 'one_time' : 'monthly',
        stripe_price_id: editingPlan.stripe_price_id || null,
        stripe_product_id: editingPlan.stripe_product_id || null,
        limits: {
          sessions_per_month: 0,  // FMS hat keine Session-Limits
          storage_gb: 10,
          max_uploads_per_session: 20
        },
        features: editingPlan.features,
        is_active: true,
        sort_order: 0,
        planType: 'fms',
        maxVehicles: parseInt(editingPlan.maxVehicles) || null,
        maxDrivers: parseInt(editingPlan.maxDrivers) || null
      };

      await axios.post(
        `${API_URL}/api/admin/plans`,
        planData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('FMS-Plan erfolgreich erstellt');
      fetchPlans();
      setShowCreateForm(false);
      setEditingPlan(null);
    } catch (error) {
      toast.error('Fehler beim Erstellen des Plans');
      console.error('Fehler beim Erstellen:', error);
    }
  };

  const handleUpdate = async () => {
    try {
      const token = localStorage.getItem('token');

      const planData = {
        id: editingPlan.id,
        name: editingPlan.name,
        price: parseFloat(editingPlan.price),
        currency: editingPlan.currency === '€' ? 'EUR' : 'USD',
        billing_interval: editingPlan.period === 'Jahr' ? 'yearly' : editingPlan.period === 'Einmalig' ? 'one_time' : 'monthly',
        stripe_price_id: editingPlan.stripe_price_id || null,
        stripe_product_id: editingPlan.stripe_product_id || null,
        limits: {
          sessions_per_month: 0,
          storage_gb: 10,
          max_uploads_per_session: 20
        },
        features: editingPlan.features,
        is_active: true,
        sort_order: 0,
        planType: 'fms',
        maxVehicles: parseInt(editingPlan.maxVehicles) || null,
        maxDrivers: parseInt(editingPlan.maxDrivers) || null
      };

      await axios.put(
        `${API_URL}/api/admin/plans/${editingPlan.id}`,
        planData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('FMS-Plan erfolgreich aktualisiert');
      fetchPlans();
      setEditingPlan(null);
    } catch (error) {
      toast.error('Fehler beim Aktualisieren des Plans');
      console.error('Fehler beim Aktualisieren:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('FMS-Plan wirklich löschen?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/plans/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Plan gelöscht');
      fetchPlans();
    } catch (error) {
      toast.error('Fehler beim Löschen');
      console.error('Fehler beim Löschen:', error);
    }
  };

  const startEdit = (plan) => {
    const featuresObj = getDefaultFeatures();
    
    // Merge Backend-Features mit Default-Features
    if (plan.features && typeof plan.features === 'object') {
      Object.assign(featuresObj, plan.features);
    }

    setEditingPlan({
      id: plan.id,
      name: plan.name,
      price: plan.price.toString(),
      currency: plan.currency === 'EUR' ? '€' : '$',
      period: plan.billing_interval === 'yearly' ? 'Jahr' : plan.billing_interval === 'one_time' ? 'Einmalig' : 'Monat',
      maxVehicles: plan.maxVehicles?.toString() || '',
      maxDrivers: plan.maxDrivers?.toString() || '',
      stripe_price_id: plan.stripe_price_id || '',
      stripe_product_id: plan.stripe_product_id || '',
      features: featuresObj,
      popular: plan.popular || false
    });
  };

  const startCreate = () => {
    setEditingPlan({
      name: '',
      price: '',
      currency: '€',
      period: 'Monat',
      maxVehicles: '',
      maxDrivers: '',
      stripe_price_id: '',
      stripe_product_id: '',
      features: getDefaultFeatures(),
      popular: false,
      planType: 'fms'
    });
    setShowCreateForm(true);
  };

  const toggleFeature = (featureKey) => {
    setEditingPlan({
      ...editingPlan,
      features: {
        ...editingPlan.features,
        [featureKey]: !editingPlan.features[featureKey]
      }
    });
  };

  if (loading) return <div className="p-8">Lädt...</div>;

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Truck className="w-8 h-8" style={{ color: '#1EA0E6' }} />
            <h1 className="text-3xl font-bold" style={{ color: '#E6EDF3' }}>
              FMS-Pläne
            </h1>
          </div>
          <p style={{ color: '#94A3B8' }}>
            Verwalten Sie die Preispläne für Flottenmanagement
          </p>
        </div>
        <Button
          onClick={startCreate}
          style={{ backgroundColor: '#1EA0E6', color: '#fff' }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Neuer FMS-Plan
        </Button>
      </div>

      {(showCreateForm || editingPlan) && (
        <Card className="mb-8" style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardHeader>
            <CardTitle style={{ color: '#E6EDF3' }}>
              {showCreateForm ? 'Neuen FMS-Plan erstellen' : 'FMS-Plan bearbeiten'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-2 text-sm font-semibold" style={{ color: '#E6EDF3' }}>
                  Plan-Name *
                </label>
                <Input
                  type="text"
                  value={editingPlan.name}
                  onChange={(e) => setEditingPlan({ ...editingPlan, name: e.target.value })}
                  placeholder="z.B. FMS Basic, FMS Pro, FMS Enterprise"
                  style={{ backgroundColor: '#1F2937', color: '#E6EDF3', borderColor: '#374151' }}
                />
              </div>
              
              <div>
                <label className="block mb-2 text-sm font-semibold" style={{ color: '#E6EDF3' }}>
                  Preis *
                </label>
                <div className="flex gap-2">
                  <select
                    value={editingPlan.currency}
                    onChange={(e) => setEditingPlan({ ...editingPlan, currency: e.target.value })}
                    className="p-2 rounded-lg"
                    style={{ backgroundColor: '#1F2937', color: '#E6EDF3', borderColor: '#374151', border: '1px solid' }}
                  >
                    <option>€</option>
                    <option>$</option>
                  </select>
                  <Input
                    type="number"
                    value={editingPlan.price}
                    onChange={(e) => setEditingPlan({ ...editingPlan, price: e.target.value })}
                    placeholder="49.99"
                    style={{ backgroundColor: '#1F2937', color: '#E6EDF3', borderColor: '#374151' }}
                  />
                </div>
              </div>

              <div>
                <label className="block mb-2 text-sm font-semibold" style={{ color: '#E6EDF3' }}>
                  Zeitraum *
                </label>
                <select
                  value={editingPlan.period}
                  onChange={(e) => setEditingPlan({ ...editingPlan, period: e.target.value })}
                  className="w-full p-2 rounded-lg"
                  style={{ backgroundColor: '#1F2937', color: '#E6EDF3', borderColor: '#374151', border: '1px solid' }}
                >
                  <option>Monat</option>
                  <option>Jahr</option>
                  <option>Einmalig</option>
                </select>
              </div>

              <div>
                <label className="block mb-2 text-sm font-semibold" style={{ color: '#E6EDF3' }}>
                  Max. Fahrzeuge
                  <span className="text-xs ml-2" style={{ color: '#94A3B8' }}>(0 = Unbegrenzt)</span>
                </label>
                <Input
                  type="number"
                  value={editingPlan.maxVehicles}
                  onChange={(e) => setEditingPlan({ ...editingPlan, maxVehicles: e.target.value })}
                  placeholder="5"
                  style={{ backgroundColor: '#1F2937', color: '#E6EDF3', borderColor: '#374151' }}
                />
              </div>

              <div>
                <label className="block mb-2 text-sm font-semibold" style={{ color: '#E6EDF3' }}>
                  Max. Fahrer
                  <span className="text-xs ml-2" style={{ color: '#94A3B8' }}>(0 = Unbegrenzt)</span>
                </label>
                <Input
                  type="number"
                  value={editingPlan.maxDrivers}
                  onChange={(e) => setEditingPlan({ ...editingPlan, maxDrivers: e.target.value })}
                  placeholder="5"
                  style={{ backgroundColor: '#1F2937', color: '#E6EDF3', borderColor: '#374151' }}
                />
              </div>
            </div>

            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editingPlan.popular}
                  onChange={(e) => setEditingPlan({ ...editingPlan, popular: e.target.checked })}
                  className="w-5 h-5"
                />
                <span style={{ color: '#E6EDF3' }}>Als "BELIEBT" markieren</span>
              </label>
            </div>

            {/* Stripe Integration */}
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#1E3A8A20', borderColor: '#3B82F6', border: '1px solid' }}>
              <div className="flex items-start gap-3">
                <div className="text-2xl">💳</div>
                <div>
                  <h4 className="font-semibold mb-1" style={{ color: '#7CD4FD' }}>
                    ✨ Automatische Stripe-Synchronisation
                  </h4>
                  <p className="text-sm mb-2" style={{ color: '#94A3B8' }}>
                    Wenn Stripe konfiguriert ist, wird automatisch ein Produkt und Preis in Ihrem Stripe-Dashboard erstellt.
                    Sie müssen keine Stripe Price ID manuell eingeben!
                  </p>
                  <p className="text-xs" style={{ color: '#6B7280' }}>
                    Optional: Geben Sie eine bestehende Stripe Price ID ein, um einen existierenden Stripe-Preis zu verwenden.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="block mb-2 text-sm font-semibold" style={{ color: '#E6EDF3' }}>
                Stripe Price ID (optional - wird automatisch erstellt)
              </label>
              <Input
                value={editingPlan.stripe_price_id || ''}
                onChange={(e) => setEditingPlan({ ...editingPlan, stripe_price_id: e.target.value })}
                placeholder="Leer lassen für automatische Erstellung"
                style={{ backgroundColor: '#1F2937', color: '#E6EDF3', borderColor: '#374151' }}
              />
              {editingPlan.stripe_price_id && (
                <p className="text-xs mt-1" style={{ color: '#10B981' }}>
                  ✓ Verwendet existierenden Stripe-Preis
                </p>
              )}
            </div>

            <div>
              <label className="block mb-2 text-sm font-semibold" style={{ color: '#E6EDF3' }}>
                Stripe Product ID (optional - wird automatisch erstellt)
              </label>
              <Input
                value={editingPlan.stripe_product_id || ''}
                onChange={(e) => setEditingPlan({ ...editingPlan, stripe_product_id: e.target.value })}
                placeholder="Leer lassen für automatische Erstellung"
                style={{ backgroundColor: '#1F2937', color: '#E6EDF3', borderColor: '#374151' }}
              />
              {editingPlan.stripe_product_id && (
                <p className="text-xs mt-1" style={{ color: '#10B981' }}>
                  ✓ Verwendet existierendes Stripe-Produkt
                </p>
              )}
            </div>

            {/* Features Selection */}
            <div className="space-y-4">
              <h3 className="text-xl font-bold mb-4" style={{ color: '#7CD4FD' }}>
                Inbegriffene Features
              </h3>

              {Object.entries(FMS_FEATURE_CATEGORIES).map(([categoryKey, category]) => (
                <div key={categoryKey} className="p-4 rounded-lg" style={{ backgroundColor: '#1E293B', borderColor: '#1F2937', border: '1px solid' }}>
                  <h4 className="text-lg font-semibold mb-3" style={{ color: '#E6EDF3' }}>
                    {category.title}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {category.features.map((feature) => (
                      <div
                        key={feature.key}
                        className="flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all hover:opacity-80"
                        onClick={() => toggleFeature(feature.key)}
                        style={{
                          backgroundColor: editingPlan.features[feature.key] ? '#1EA0E6' : '#0B0F14',
                          borderColor: '#1F2937',
                          border: '1px solid'
                        }}
                      >
                        <div className="flex-shrink-0 mt-1">
                          {editingPlan.features[feature.key] ? (
                            <Check className="w-5 h-5 text-white" />
                          ) : (
                            <X className="w-5 h-5 text-gray-500" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-sm" style={{ color: editingPlan.features[feature.key] ? '#FFFFFF' : '#E6EDF3' }}>
                            {feature.label}
                          </div>
                          <div className="text-xs mt-1" style={{ color: editingPlan.features[feature.key] ? '#E6EDF3' : '#94A3B8' }}>
                            {feature.description}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={showCreateForm ? handleCreate : handleUpdate}
                style={{ backgroundColor: '#22C55E', color: '#fff' }}
              >
                <Check className="w-4 h-4 mr-2" />
                {showCreateForm ? 'Erstellen' : 'Aktualisieren'}
              </Button>
              <Button
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingPlan(null);
                }}
                style={{ backgroundColor: '#374151', color: '#E6EDF3' }}
              >
                <X className="w-4 h-4 mr-2" />
                Abbrechen
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <Card
            key={plan.id}
            style={{
              backgroundColor: '#0F141A',
              borderColor: plan.popular ? '#1EA0E6' : '#1F2937',
              borderWidth: plan.popular ? '2px' : '1px'
            }}
          >
            <CardContent className="p-6">
              {plan.popular && (
                <div className="mb-3 px-3 py-1 rounded-full inline-block text-xs font-bold" style={{ backgroundColor: '#1EA0E6', color: '#fff' }}>
                  BELIEBT
                </div>
              )}
              <div className="flex items-center gap-2 mb-2">
                <Truck className="w-5 h-5" style={{ color: '#1EA0E6' }} />
                <h3 className="text-2xl font-bold" style={{ color: '#E6EDF3' }}>
                  {plan.name}
                </h3>
              </div>
              <div className="mb-4">
                <span className="text-4xl font-bold" style={{ color: '#7CD4FD' }}>
                  {plan.currency}{plan.price}
                </span>
                <span style={{ color: '#94A3B8' }}>/{plan.period}</span>
              </div>
              <div className="text-sm mb-2" style={{ color: '#94A3B8' }}>
                <strong>Fahrzeuge:</strong> {plan.maxVehicles > 0 ? plan.maxVehicles : 'Unbegrenzt'}
              </div>
              <div className="text-sm mb-4" style={{ color: '#94A3B8' }}>
                <strong>Fahrer:</strong> {plan.maxDrivers > 0 ? plan.maxDrivers : 'Unbegrenzt'}
              </div>

              {/* Features anzeigen */}
              {plan.features && plan.features.length > 0 && (
                <div className="mb-4 space-y-2">
                  {plan.features.slice(0, 3).map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs" style={{ color: '#94A3B8' }}>
                      <Check className="w-4 h-4" style={{ color: '#22C55E' }} />
                      <span>{feature}</span>
                    </div>
                  ))}
                  {plan.features.length > 3 && (
                    <div className="text-xs" style={{ color: '#7CD4FD' }}>
                      +{plan.features.length - 3} weitere Features
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-2 mt-4">
                <Button
                  size="sm"
                  onClick={() => startEdit(plan)}
                  style={{ backgroundColor: '#1EA0E6', color: '#fff' }}
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleDelete(plan.id)}
                  style={{ backgroundColor: '#EF4444', color: '#fff' }}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {plans.length === 0 && !showCreateForm && (
        <div className="text-center py-20">
          <Truck className="w-16 h-16 mx-auto mb-4" style={{ color: '#374151' }} />
          <p className="text-xl mb-2" style={{ color: '#94A3B8' }}>
            Noch keine FMS-Pläne erstellt
          </p>
          <p style={{ color: '#6B7280' }}>
            Klicken Sie auf "Neuer FMS-Plan", um zu beginnen
          </p>
        </div>
      )}
    </div>
  );
};
